import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  Provider,
  Signal,
  signal, WritableSignal
} from '@angular/core';
import {Analytics} from "../analytics/analytics.service";
import {
  CaptureError,
  genAddress,
  genEmail,
  genPhone,
  NotificationType,
  PhoneType,
  Schema
} from "@mindpath/shared";
import {config} from "../../config";
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {apiUrl} from "../networking";
import {assertExists, assertTruthy} from "../assert";
import {DemographicsInfo} from "../statics";
import {ReferralSourceService} from "../services/referral-source.service";
import * as Sentry from '@sentry/angular';
import {getGlobalScope, Span} from "@sentry/angular";

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  public static readonly Data = new InjectionToken<Signal<AuthData | undefined>>('AuthData');
  readonly #data$: WritableSignal<AuthData | undefined>;
  readonly user = computed<AuthData['user'] | undefined>(() => this.#data$()?.user);
  readonly session = computed<AuthData['session']>(() => this.#data$()?.session);
  readonly intake = computed<AuthData['intake']>(() => this.#data$()?.intake);

  #initPromise: Promise<AuthItem> | undefined;
  #http = inject(HttpClient);
  #referral = inject(ReferralSourceService);
  #refreshIntervals: {
    session?: number;
    intake?: number;
  } = {};

  readonly data: Signal<AuthData | undefined>;
  constructor() {
    this.#data$ = signal<AuthData | undefined>(this.#load());
    this.data = this.#data$.asReadonly();
  }

  resetIntakeAuth() {
    // clear out the intake auth data only
    this.#data$.update(data => {
      if (!data || !data.intake) {
        return data;
      }
      return {
        ...data,
        intake: undefined,
      }
    });
    this.#save();
  }

  #load() {
    const userStr = localStorage.getItem('auth');
    if (userStr) {
      const sessionStr = sessionStorage.getItem('auth');
      const intakeStr = sessionStorage.getItem('auth-intake');
      const user = JSON.parse(userStr);
      const session = sessionStr ? JSON.parse(sessionStr) : undefined;
      const intake = intakeStr ? JSON.parse(intakeStr) : undefined;
      return {
        user,
        session,
        intake,
      };
    } else {
      // if no user auth is found, clear the session data
      sessionStorage.clear();
    }
    return undefined;
  }

  #save() {
    const data = this.#data$();
    if (data) {
      localStorage.setItem('auth', JSON.stringify(data.user));
    } else {
      localStorage.removeItem('auth');
    }

    if (data?.session) {
      sessionStorage.setItem('auth', JSON.stringify(data.session));
    } else {
      sessionStorage.removeItem('auth');
    }

    if (data?.intake) {
      sessionStorage.setItem('auth-intake', JSON.stringify(data.intake));
    } else {
      sessionStorage.removeItem('auth-intake');
    }
  }

  validateAppointmentOtp(otp: string) {
    return Sentry.startSpan({
      name: 'Validate OTP',
      op: 'auth.validateOtp',
    }, async span => {

      const data = this.#data$();
      assertExists(data);
      assertExists(data.user);
      span.setAttribute('userId', data.user.id);
      assertExists(data.session);
      span.setAttribute('sessionId', data.session.id);
      const intakeId = data?.intake?.id;
      assertExists(intakeId);
      span.setAttribute('intakeId', intakeId);


      const resp = await lastValueFrom(this.#http.post<Token>(apiUrl`${config}/appointments/${intakeId}/verification`, {
        otpCode: otp,
      }));

      this.#data$.update(data => {
        assertExists(data?.intake);
        return {
          ...data,
          intake: {
            ...data.intake,
            auth: resp
          }
        }
      });
      this.#save();
    });
  }

  updateIntakeToken(token: Token) {
    this.#data$.update(data => {
      assertExists(data);
      return {
        ...data,
        intake: {
          ...data.intake!,
          auth: token,
        }
      }
    });
    this.#save();
  }

  async reRequestAppointmentOtp(patient: DemographicsInfo) {
    const data = this.#data$()?.intake?.otp;
    assertExists(data);
    await this.requestAppointmentOtp(data.destination, data.destinationType, patient);
  }

  requestAppointmentOtp(destination: string, destinationType: 'sms' | 'email' | 'phone', patient: DemographicsInfo) {
    // this.#uiSvc.showLoading('Sending Code', 'Please wait while we send you a verification code');
    return Sentry.startSpan({
      name: 'Request OTP',
    }, async span => {
      const data = this.#data$();
      assertExists(data);
      assertExists(data.user);
      span.setAttribute('userId', data.user.id);
      assertExists(data.session);
      span.setAttribute('sessionId', data.session.id);

      const intakeId = data.intake?.id;
      span.setAttribute('intakeId', intakeId);

      const body: Schema<'IntakeInitializationRequest'> = {
        otpDestination: destination,
        otpTarget: destinationType as NotificationType,
        patient: this.#buildPatient(patient),
      };

      const resp = await lastValueFrom(this.#http.post<Schema<'IExposedIntakeRequestInfo'>>(apiUrl`${config}/appointments`, body));


      const intakeOtp = {
        id: resp.id,
        otp: {
          destination,
          destinationType,
          requestedAt: Date.now(),
          auth: {
            token: resp.token,
            expiresAt: resp.expiresAt,
          },
        }
      };

      this.#data$.update(data => {
        assertExists(data);
        return {
          ...data,
          intake: intakeOtp,
        }
      });
      this.#save();
    });
  }

  /**
   * Starts the intake for a referral visit scheduling on behalf of the patient. With an active
   * `referralSourceId` and `isSchedulingOnBehalfOf` the API sends no code and answers with the booking
   * token, so the intake is authorized straight away and has no `otp` step.
   */
  startReferralIntake(patient: DemographicsInfo) {
    return Sentry.startSpan({
      name: 'Start Referral Intake',
    }, async span => {
      assertTruthy(this.#referral.schedulingOnBehalfOf());
      const referralSourceId = this.#referral.referralSourceId();
      assertExists(referralSourceId);

      const data = this.#data$();
      assertExists(data);
      assertExists(data.user);
      span.setAttribute('userId', data.user.id);
      assertExists(data.session);
      span.setAttribute('sessionId', data.session.id);

      // `otpTarget` is required by the request record and ignored when the API skips the code.
      const body: Schema<'IntakeInitializationRequest'> & {
        readonly referralSourceId?: string;
        readonly isSchedulingOnBehalfOf?: boolean;
      } = {
        otpTarget: 'email' as NotificationType,
        patient: this.#buildPatient(patient),
        referralSourceId,
        // Always true here (asserted above). The API skips the code only for a referral scheduled on behalf.
        isSchedulingOnBehalfOf: this.#referral.schedulingOnBehalfOf(),
      };

      const resp = await lastValueFrom(this.#http.post<Schema<'IExposedIntakeRequestInfo'>>(apiUrl`${config}/appointments`, body));
      span.setAttribute('intakeId', resp.id);

      this.#data$.update(data => {
        assertExists(data);
        return {
          ...data,
          intake: {
            id: resp.id,
            auth: {
              token: resp.token,
              expiresAt: resp.expiresAt,
            },
            referralPatientKey: referralPatientKey(patient),
          },
        }
      });
      this.#save();
    });
  }

  #buildPatient(patient: DemographicsInfo): Schema<'IntakeInitializationRequest'>['patient'] {
    return {
      dateOfBirth: patient.dob!.toISODate(),
      contactDetails: {
        firstName: patient.firstName!,
        lastName: patient.lastName!,
        phoneNumbers: [
          genPhone(PhoneType.Mobile, patient.phone!)
        ],
        emails: [
          genEmail(patient.email!)
        ],
        addresses: [genAddress(patient.address!)]
      },
      sexAtBirth: patient.sex!,
    };
  }


  async load() {
    return this.init();
  }

  checkDeviceOtpIsDifferent(destination: { phone?: string, email?: string, intakeEmail?: string | null }) {
    const data = this.#data$()?.intake?.otp;
    if (!data) {
      return true;
    }
    const previousDestination = data.destination;
    const previousType = data.destinationType;

    switch (previousType) {
      case 'phone':
      case 'sms': {
        let phoneNumber = destination.phone;
        if (!phoneNumber) {
          return false;
        }
        if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
          phoneNumber = phoneNumber.slice(1);
        }
        return phoneNumber !== previousDestination;
      }
      case 'email': {
        let email = destination.email;
        if (data.isIntakeEmail) {
          email = destination.email;
        }
        return email?.toLowerCase() !== previousDestination.toLowerCase();
      }
    }
  }

  checkValidity(target: 'otp', data: { phone?: string, email?: string, intakeEmail?: string }): boolean;
  checkValidity(target: keyof AuthData): boolean;
  checkValidity(target: keyof AuthData | 'otp', data?: {
    phone?: string,
    email?: string,
    intakeEmail?: string
  }): boolean {
    const targetData = target === 'otp' ? this.#data$()?.intake?.otp : this.#data$()?.[target];
    if (!targetData) {
      return false;
    }

    const auth = targetData.auth;
    if (!auth?.token?.length) {
      return false;
    }
    const now = Date.now();
    // if expired or expires in the next 5 seconds, return
    if (auth.expiresAt - now < 5000) {
      return false;
    }

    // if we're not checking the intake auth data, return true
    if (target !== 'otp') {
      return true;
    }
    if (!data) {
      return true;
    }
    return !this.checkDeviceOtpIsDifferent(data);
  }


  itemStillValid(item: Partial<{ auth: Token }> | undefined): item is AuthItem {
    if (!item?.auth?.token || !item?.auth?.expiresAt) {
      return false;
    }
    const now = Date.now();
    // if expired or expires in the next 5 seconds, return false
    return item.auth.expiresAt - now > 5000;
  }

  static provideUser(): Provider[] {
    return [
      {
        provide: AuthService.Data,
        useFactory: (auth: AuthService) => auth.#data$.asReadonly(),
        deps: [AuthService],
      }
    ]
  }


  init() {
    if (this.#initPromise) {
      return this.#initPromise;
    }

    const data = this.#data$();
    if (!this.itemStillValid(data?.user) || !this.itemStillValid(data?.session)) {
      this.#initPromise = this.#createOrRefreshTokens();
    } else {
      this.#setSentryUser();
      this.#initPromise = Promise.resolve(data!.session ?? data!.user);
      this.#createOrRefreshTokens().then(data => this.#initPromise = Promise.resolve(data)).catch(e => {
        CaptureError(e, {
          details: 'Error kicking off token refresh'
        });
      });
    }

    return this.#initPromise;
  }

  setIntakeEmailUsage(usage: boolean) {
    this.#data$.update(data => {
      assertExists(data);
      return {
        ...data,
        intake: {
          ...(data.intake ?? {}),
          otp: {
            ...(data.intake?.otp ?? {}) as NonNullable<NonNullable<AuthData['intake']>['otp']>,
            isIntakeEmail: usage,
          }
        }
      }
    });
  }

  #onNewUserCallbacks: ((data: AuthData) => void)[] = [];

  onNewUser(callback: (data: AuthData) => void) {
    this.#onNewUserCallbacks.push(callback);
    return () => {
      this.#onNewUserCallbacks = this.#onNewUserCallbacks.filter(cb => cb !== callback);
    };
  }

  #loadReferringSourceHeaders() {
    const headers: Record<string, string> = {}
    if (window._MindPath.referring.source) {
      headers['X-Referring-Source'] = window._MindPath.referring.source;
    }

    if (window._MindPath.referring.entryUrl) {
      headers['X-Referring-Source-Entry-Url'] = window._MindPath.referring.entryUrl;
    }

    if (window._MindPath.referring.error) {
      CaptureError(window._MindPath.referring.error);
      window._MindPath.referring.error = undefined;
    }
    return headers;
  }

  #getNewUserToken(parentSpan: Span): Promise<AuthData> {
    return Sentry.startSpan({
        name: 'Get New User Token',
        op: 'auth.getNewUserToken',
        parentSpan
      },
      async () => {
        const url = `${config.endpoints.api}/api/finder`;

        const userData = await lastValueFrom(this.#http.post<AuthData>(url, {}, {
          headers: this.#loadReferringSourceHeaders(),
        }));
        this.#data$.set({
          ...userData,
        });
        this.#save();
        this.#onNewUserCallbacks.forEach(cb => {
          try {
            cb({...userData});
          } catch (e) {
            CaptureError(e, {
              details: 'Error in onNewUser callback'
            });
          }
        });
        return userData;
      })
  }

  #refreshSessionToken(parentSpan: Span, session: AuthItem) {
    return Sentry.startSpan({
      name: 'Refresh Session Token',
      op: 'auth.refreshSessionToken',
      parentSpan
    }, async span => {
      span.setAttribute('sessionId', session.id);

      const url = `${config.endpoints.api}/api/finder/${session.id}`;
      const newData = await lastValueFrom(this.#http.get<AuthData>(url, {
        headers: {
          Authorization: `Bearer ${session.auth.token}`,
          ...this.#loadReferringSourceHeaders(),
        }
      }));

      this.#data$.update(data => {
        return {
          ...(data ?? {}),
          ...newData,
        }
      });
      this.#save();
      return newData.session;
    });
  }

  #getNewSessionToken(parentSpan: Span, user: AuthItem) {
    return Sentry.startSpan({
      name: 'Get New Session Token',
      op: 'auth.getNewSessionToken',
      parentSpan
    }, async span => {
      const url = `${config.endpoints.api}/api/finder`;

      span.setAttributes({
        userId: user.id,
      });


      const newData = await lastValueFrom(this.#http.put<AuthData>(url, {}, {
        headers: {
          Authorization: `Bearer ${user.auth.token}`,
          ...this.#loadReferringSourceHeaders(),
        },
      }));

      this.#data$.update(data => {
        return {
          ...(data ?? {}),
          ...newData,
        }
      });
      this.#save();
      return newData.session;
    });
  }

  #setSentryUser() {
    const data = this.#data$();
    getGlobalScope().setUser({
      id: data?.user.id,
      sessionId: data?.session?.id,
    });
    getGlobalScope().setTag('sessionId', data?.session?.id);
  }

  #createOrRefreshTokens(): Promise<AuthItem> {

    return Sentry.startSpan({
        name: 'Create or Refresh Tokens',
        op: 'auth.createOrRefreshTokens',
      },
      async span => {


        if (this.#refreshIntervals.session) {
          clearInterval(this.#refreshIntervals.session);
        }

        let data = this.#data$();


        let session = data?.session;
        this.#setSentryUser();
        if (!data?.user || !this.itemStillValid(data.user)) {
          Sentry.addBreadcrumb({
            message: 'User token not set or expired, getting new user token',
            level: 'debug',
            category: 'auth.token',
            timestamp: Date.now(),
            data: {
              expiresAt: data?.user?.auth?.expiresAt,
              expiresIn: data?.user?.auth?.expiresAt ? data.user.auth.expiresAt - Date.now() : undefined,
            }
          });
          data = await this.#getNewUserToken(span);
          session = data.session;
          this.#setSentryUser();
          span.setAttribute('userId', data.user.id);
        } else if (data && (!session || !this.itemStillValid(session))) {
          Sentry.addBreadcrumb({
            message: 'Session token not set or expired, getting new session token',
            level: 'debug',
            category: 'auth.token',
            timestamp: Date.now(),
            data: {
              expiresAt: data?.session?.auth?.expiresAt,
              expiresIn: data?.session?.auth?.expiresAt ? data.session.auth.expiresAt - Date.now() : undefined,
            }
          });
          session = await this.#getNewSessionToken(span, data.user);
          this.#setSentryUser();
        } else if (session) {
          Sentry.addBreadcrumb({
            message: 'Session token is set, refreshing session token',
            level: 'debug',
            category: 'auth.token',
            timestamp: Date.now(),
            data: {
              expiresAt: session.auth.expiresAt,
              expiresIn: session.auth.expiresAt - Date.now(),
            }
          });
          session = await this.#refreshSessionToken(span, session);
          this.#setSentryUser();
        }

        assertExists(session);
        span.setAttribute('sessionId', session.id);

        const earliestExpiresAt = Math.min(data.user.auth.expiresAt, session?.auth.expiresAt ?? Infinity);
        // refresh 1 minute before the earliest user/session token expires
        const refreshAt = earliestExpiresAt - Date.now() - 60_000;
        Sentry.addBreadcrumb(
          {
            category: 'auth.token',
            message: `Refreshing tokens in ${refreshAt} ms`,
            level: 'info',
            type: 'info',
            data: {
              earliestExpiresAt,
              user: data.user.auth.expiresAt,
              session: session?.auth.expiresAt,
              specifically: earliestExpiresAt - Date.now(),
              now: Date.now(),
            },
            timestamp: Date.now(),
          });

        this.#refreshIntervals.session = setInterval(() => {
          getGlobalScope().addBreadcrumb({
            message: 'Refreshing session token due to near expiration',
            level: 'info',
            category: 'auth.token',
            timestamp: Date.now(),
            data: {
              refreshedAfter: refreshAt,
              expiresAt: session?.auth.expiresAt,
              expiresIn: session?.auth.expiresAt ? session.auth.expiresAt - Date.now() : undefined,
            }
          });
          this.#createOrRefreshTokens();
        }, refreshAt) as unknown as number;

        return session ?? data.user;
      });
  }
}

/**
 * Who a referral intake was started for. A different patient needs a new intake request, not an update
 * of the previous one.
 */
export function referralPatientKey(patient: Pick<DemographicsInfo, 'firstName' | 'lastName' | 'dob'>): string {
  return [patient.firstName?.trim(), patient.lastName?.trim(), patient.dob?.toISODate()]
    .map(part => part ?? '')
    .join('|')
    .toLowerCase();
}

interface Token {
  readonly token: string;
  readonly expiresAt: number;
}

export interface AuthItem {
  readonly id: Analytics.Id;
  readonly auth: Token;
}

export interface AuthData {
  readonly user: {
    readonly id: Analytics.Id;
    readonly auth: Token;
  }
  readonly session?: {
    readonly id: Analytics.Id;
    readonly auth: Token;
  }
  readonly intake?: {
    readonly id?: Analytics.Id;
    readonly completedAt?: number;
    /** Set by `startReferralIntake`; see `referralPatientKey`. */
    readonly referralPatientKey?: string;
    /** Absent for an intake that never needed a code (scheduling on behalf of the patient). */
    readonly otp?: {
      readonly isIntakeEmail?: boolean;
      readonly destination: string;
      readonly destinationType: 'sms' | 'email' | 'phone';
      readonly requestedAt: number;
      readonly auth?: Token;
    },
    readonly auth?: Token;
  }
}
